<script lang="ts">
  import type { Dataset, Era } from '../types.ts';
  import { fmtDate } from '../format.ts';

  let { ds }: { ds: Dataset } = $props();
  let era = $state<'all' | Era>('all');
  let q = $state('');
  let flaggedOnly = $state(false);
  const rows = $derived([...ds.visits].reverse().filter((v) =>
    (era === 'all' || v.era === era) && (!flaggedOnly || v.flags.length) && (!q.trim() || (v.note + ' ' + v.flags.join(' ')).toLowerCase().includes(q.trim().toLowerCase()))));
  const tone = (f: string) => (/untreated|≥ 8\.5/.test(f) ? 'danger' : 'warn');
</script>

<div class="notes">
  <div class="bar">
    <div class="seg" role="tablist">
      <button class:on={era === 'all'} onclick={() => (era = 'all')}>All eras</button>
      {#each ds.summary.eras as e (e.era)}<button class:on={era === e.era} onclick={() => (era = e.era)}>{e.label}</button>{/each}
    </div>
    <label class="chk"><input type="checkbox" bind:checked={flaggedOnly} /> flagged visits only</label>
    <input class="search" type="search" placeholder="Search notes… (e.g. semaglutide, nocturnal, UACR)" bind:value={q} />
    <span class="t-foot">{rows.length} of {ds.visits.length} visits</span>
  </div>

  <ol class="list">
    {#each rows as v (v.index)}
      <li>
        <div class="when">
          <span class="d">{fmtDate(v.date)}</span>
          <span class="t-foot">visit {v.index + 1} · age {Math.floor(v.ageYears)}</span>
          <span class="era">{v.eraLabel}</span>
        </div>
        <div class="body">
          <p>{v.note}</p>
          <div class="chips">
            {#each v.flags as f (f)}<span class="flag {tone(f)}">{f}</span>{/each}
            <span class="vals t-foot">HbA1c {v.glycemic.hba1c}% · BP {v.vitals.systolic}/{v.vitals.diastolic} · {v.vitals.weightKg} kg{#if v.lipids} · LDL {v.lipids.ldl} · TG {v.lipids.tg}{/if}{#if v.renal.uacr != null} · UACR {v.renal.uacr}{/if}</span>
          </div>
          <div class="medsline t-foot">On: {v.medications.map((m) => `${m.name} ${m.dose}`).join(' · ')}</div>
        </div>
      </li>
    {/each}
  </ol>
</div>

<style>
  .notes { display: flex; flex-direction: column; gap: var(--space-4); }
  .bar { display: flex; align-items: center; gap: var(--space-4); flex-wrap: wrap; }
  .seg { display: inline-flex; border: 1px solid var(--line-strong); border-radius: var(--radius-pill); overflow: hidden; }
  .seg button { padding: var(--space-1) var(--space-3); font-size: var(--fs-foot); font-weight: 700; color: var(--text-2); }
  .seg button.on { background: var(--accent); color: var(--on-accent); }
  .chk { display: inline-flex; align-items: center; gap: 6px; color: var(--text-2); font-size: var(--fs-foot); }
  .search { flex: 1; min-width: 220px; padding: var(--space-2) var(--space-3); border: 1px solid var(--line-strong); border-radius: var(--radius-pill); background: var(--surface); }
  .list { list-style: none; display: flex; flex-direction: column; gap: var(--space-2); }
  li { display: grid; grid-template-columns: 150px 1fr; gap: var(--space-4); background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius-md); padding: var(--space-3) var(--space-4); }
  @media (max-width: 700px) { li { grid-template-columns: 1fr; gap: var(--space-2); } }
  .when { display: flex; flex-direction: column; gap: 2px; }
  .d { font-weight: 700; }
  .era { font-size: var(--fs-foot); color: var(--accent); font-weight: 700; }
  .body p { line-height: 1.6; }
  .chips { display: flex; gap: 6px; flex-wrap: wrap; align-items: center; margin-top: 6px; }
  .flag { font-size: var(--fs-foot); font-weight: 700; border-radius: var(--radius-pill); padding: 1px 8px; }
  .flag.warn { color: var(--warn); background: var(--warn-soft); }
  .flag.danger { color: var(--danger); background: var(--danger-soft); }
  .medsline { margin-top: 4px; }
</style>
